import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Stack, Typography, IconButton } from '@mui/material';
import { useDataContext } from '../Context/useDataContext';
import { useLSContext } from '../Context/useLSContext';
import { useHoleSettings } from '../Context/useHoleSettingsContext';
import { ArrowDownward, ArrowUpward, SaveOutlined } from '@mui/icons-material';
import { usePendingScoreContext } from '../Context/PendingScoreContext';
import { buildHoleEnqueueItems } from '../utils/enqueueHoleScoreUpdates';

interface NumPadProps {
    highScore: number;
    lowScore: number;
    reducedRegistrations: any;
    disabled?: boolean;
    onScoreChange?: (updatedScoreData: any[]) => void;
    fullWidth?: boolean;
}

const NumPad = ({ highScore, lowScore, reducedRegistrations, disabled = false, onScoreChange, fullWidth = false }: NumPadProps) => {
    const { scoreBlockID, setScoreBlockID } = useLSContext();
    const { eventData, scoreData, currentRound, scoreCode, setScoreData, scoreDataRef } = useDataContext();
    const { holeSettings } = useHoleSettings();
    const { enqueue, flushQueue } = usePendingScoreContext();

    const [blockIndex, setBlockIndex] = useState(0);
    const [customScore, setCustomScore] = useState(9);

    const roundTotalHoles = useMemo(() => {
        const fromRound = Number(eventData?.rounds?.[currentRound - 1]?.totalHoles);
        const fromEvent = Number(eventData?.totalHoles);
        return (Number.isFinite(fromRound) && fromRound > 0 ? fromRound : 0)
            || (Number.isFinite(fromEvent) && fromEvent > 0 ? fromEvent : 0)
            || 18;
    }, [eventData?.rounds, eventData?.totalHoles, currentRound]);

    // Get unique score codes from reducedRegistrations
    const uniqueScoreCodes = useMemo(() => {
        return reducedRegistrations.map((reg: any) => reg.scoreCode);
    }, [reducedRegistrations]);

    // Effective max for the actively selected score block = base max + that player's handicap strokes on this hole.
    // When the event has no maxHoleScoreInfo, FloatingActionBar passes highScore=0; the grid still shows 1–8, so use the same fallback here — otherwise Math.min(score, 0) clamps every numpad entry to 1.
    const effectiveHighScore = useMemo(() => {
        const baseMax = highScore > 0 ? highScore : 8;
        if (reducedRegistrations.length === 0 || blockIndex >= reducedRegistrations.length) return baseMax;
        const blockScoreCode = reducedRegistrations[blockIndex]?.scoreCode;
        const marker = eventData?.scoreType?.toLowerCase() === 'opponent' ? blockScoreCode === scoreCode : false;
        const eventScore = eventData?.scoreType?.toLowerCase() === 'opponent'
            ? scoreData?.[0]
            : scoreData?.find((s: any) => s.playerScoreCode === blockScoreCode);
        const roundScore = eventScore?.roundScores?.find((r: any) => r.roundNumber === currentRound);
        const holeScore = roundScore?.holeScores?.find((h: any) => h.holeNumber === holeSettings.hole);
        const hcpStrokes = holeScore ? (marker ? (holeScore.markerHcpAdj ?? 0) : (holeScore.playerHcpAdj ?? 0)) : 0;
        return baseMax + hcpStrokes;
    }, [highScore, reducedRegistrations, blockIndex, eventData, scoreCode, scoreData, currentRound, holeSettings.hole]);

    // console.log('uniqueScoreCodes:', uniqueScoreCodes);

    useEffect(() => {
        if (uniqueScoreCodes[blockIndex]) {
            setScoreBlockID(uniqueScoreCodes[blockIndex]);
        }
    }, [blockIndex, uniqueScoreCodes, setScoreBlockID]);

    useEffect(() => {
        if (!scoreBlockID) return;
        const idx = uniqueScoreCodes.indexOf(scoreBlockID);
        if (idx !== -1) {
            setBlockIndex((prev) => (prev !== idx ? idx : prev));
        }
    }, [scoreBlockID, uniqueScoreCodes]);

    useEffect(() => {
        setBlockIndex(0);
    }, [holeSettings.hole]);

    useEffect(() => {
        if (effectiveHighScore > 0 && customScore > effectiveHighScore) {
            setCustomScore(effectiveHighScore);
        }
    }, [effectiveHighScore, customScore]);
    
    const onNumberClick = (number: number) => {
        if (disabled) return;
        handleScore_Submit(number);
        if (number !== 0) onArrowDownClick();
    }

    const onArrowUpClick = () => {
        if (disabled) return;
        const newIndex = blockIndex > 0 ? blockIndex - 1 : 0;
        setBlockIndex(newIndex);
    }

    const onArrowDownClick = () => {
        if (disabled) return;
        const newIndex = blockIndex < uniqueScoreCodes.length - 1 ? blockIndex + 1 : blockIndex;
        setBlockIndex(newIndex);
    }

    const handleScore_Submit = (newScore: number) => {
        if (disabled) return;
        const blockScoreCode = reducedRegistrations[blockIndex]?.scoreCode;
        if (!blockScoreCode) return;
        const valueToWrite = newScore === 0 ? 0 : Math.max(1, Math.min(newScore, effectiveHighScore));

        const marker = eventData.scoreType?.toLowerCase() === 'opponent' ? blockScoreCode === scoreCode : false;
        const scoreKey = marker ? 'markerScore' : 'playerScore';

        const nextScoreData = scoreData.map((eventScore: any) => {
            if (eventData.scoreType?.toLowerCase() === 'opponent') {
                if (eventScore !== scoreData[0]) return eventScore;
            } else {
                if (eventScore.playerScoreCode !== blockScoreCode) return eventScore;
            }
            return {
                ...eventScore,
                roundScores: eventScore.roundScores.map((roundScore: any) => {
                    if (roundScore.roundNumber !== currentRound) return roundScore;
                    return {
                        ...roundScore,
                        holeScores: roundScore.holeScores.map((hole: any) => {
                            if (hole.holeNumber !== holeSettings.hole) return hole;
                            return {
                                ...hole,
                                [scoreKey]: valueToWrite
                            };
                        })
                    };
                })
            };
        });

        setScoreData(nextScoreData);
        onScoreChange?.(nextScoreData);
    }

    const handleForceSave = useCallback(() => {
        if (disabled) return;
        const items = buildHoleEnqueueItems({
            scoreData,
            scoreDataRef,
            holeNumber: holeSettings.hole,
            currentRound,
            scoreCode,
            eventData,
            roundTotalHoles,
            force: true,
        });
        if (items.length > 0) {
            enqueue(items);
            void flushQueue();
        }
        scoreDataRef.current = JSON.parse(JSON.stringify(scoreData));
    }, [
        disabled,
        scoreData,
        scoreDataRef,
        holeSettings.hole,
        currentRound,
        scoreCode,
        eventData,
        roundTotalHoles,
        enqueue,
        flushQueue,
    ]);

    const disabledSx = disabled ? {
        opacity: 0.45,
        pointerEvents: 'none' as const,
    } : {};

    return (
       <Stack direction="row" spacing={1}
            sx={{
                width: fullWidth ? '95%' : '80%',
                maxWidth: fullWidth ? 'none' : '400px',
                alignSelf: fullWidth ? 'center' : undefined,
                borderRadius: 2,
                backgroundColor: 'sepia.background',
                border: '1px solid',
                borderColor: 'sepia.extraLight',
                padding: 0.5,
                overflow: 'hidden',
                ...disabledSx,
            }}
        >

            <Stack direction="column" gap={'2px'} flex={3}>
                {Array.from({ length: Math.ceil((Math.min(8, effectiveHighScore === 0 ? 8 : effectiveHighScore) - lowScore + 1) / 4) }, (_, rowIndex) => (
                    <Stack key={rowIndex} direction="row" gap={'2px'} width={'100%'}>
                        {Array.from({ length: Math.min(4, Math.min(8, effectiveHighScore === 0 ? 8 : effectiveHighScore) - lowScore + 1 - rowIndex * 4) }, (_, colIndex) => {
                            const number = lowScore + rowIndex * 4 + colIndex;
                            return (
                                <Stack key={number} direction="column" gap={'2px'} flex={1}>
                                    <Button
                                        variant="contained"
                                        onClick={() => onNumberClick(number)}
                                        sx={{
                                            height: 48,
                                            minWidth: 0,
                                            padding: 0,
                                            fontSize: 20,
                                            fontWeight: 600,
                                            color: 'sepia.dark',
                                            borderRadius: 2,
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'white',
                                            border: '1px solid',
                                            borderColor: 'sepia.extraLight',
                                            boxShadow: 'none',
                                            '&:hover': { backgroundColor: 'sepia.background', boxShadow: 'none' },
                                        }}
                                    >
                                        {number}
                                        {number === holeSettings.par && (
                                            <Typography position="absolute" variant="body1"
                                                sx={{ fontSize: 11, color: 'sepia.main', transform: 'translateY(100%)' }}>
                                                Par
                                            </Typography>
                                        )}
                                    </Button>
                                </Stack>
                            );
                        })}
                    </Stack>
                ))}

                <Stack direction="row" gap={'2px'}>
                    <Button
                        key={'clear-score'}
                        variant="contained"
                        onClick={() => onNumberClick(0)}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            height: 41,
                            padding: 0,
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'white',
                            borderRadius: 2,
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'sepia.light',
                            border: '1px solid',
                            borderColor: 'sepia.main',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: 'sepia.main', boxShadow: 'none' },
                        }}
                    >
                        Clear
                    </Button>

                    {(effectiveHighScore === 0 || effectiveHighScore > 8) && (
                        <Stack direction="row" gap={'0px'} borderRadius={2} flex={1} height={40}
                            sx={{ border: '1px solid', borderColor: 'sepia.extraLight', overflow: 'hidden' }}
                        >
                            <IconButton
                                sx={{
                                    padding: 1,
                                    fontWeight: 'bold',
                                    backgroundColor: 'sepia.extraLight',
                                    borderRadius: 0,
                                    width: 24,
                                    height: '100%',
                                    fontSize: 18,
                                    color: 'sepia.dark',
                                    borderRight: '1px solid',
                                    borderColor: 'sepia.extraLight',
                                }}
                                onClick={() => setCustomScore(customScore > 9 ? customScore - 1 : 9)}
                            >
                                -
                            </IconButton>
                            <Button
                                key={'custom-score'}
                                variant="contained"
                                onClick={() => onNumberClick(customScore)}
                                sx={{
                                    width: '100%',
                                    minWidth: 0,
                                    height: '100%',
                                    padding: 0,
                                    fontSize: 18,
                                    fontWeight: 600,
                                    color: 'sepia.dark',
                                    borderRadius: 0,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'white',
                                    boxShadow: 'none',
                                    '&:hover': { backgroundColor: 'sepia.background', boxShadow: 'none' },
                                }}
                            >
                                {customScore}
                            </Button>
                            <IconButton
                                sx={{
                                    padding: 1,
                                    fontWeight: 'bold',
                                    backgroundColor: 'sepia.extraLight',
                                    borderRadius: 0,
                                    width: 24,
                                    height: '100%',
                                    fontSize: 18,
                                    color: 'sepia.dark',
                                    borderLeft: '1px solid',
                                    borderColor: 'sepia.extraLight',
                                }}
                                onClick={() => setCustomScore(effectiveHighScore === 0 || customScore < effectiveHighScore ? customScore + 1 : effectiveHighScore)}
                            >
                                +
                            </IconButton>
                        </Stack>
                    )}
                </Stack>
            </Stack>

            <Stack direction="column" gap={'2px'} flex={1}>
                <Button variant="contained" onClick={onArrowUpClick}
                    sx={{
                        width: '100%',
                        minWidth: 0,
                        height: 48,
                        maxHeight: 48,
                        padding: 0,
                        borderRadius: 2,
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'primary.light',
                        border: '1px solid',
                        borderColor: 'primary.dark',
                        boxShadow: 'none',
                        color: 'white',
                        '&:hover': { backgroundColor: 'primary.light', boxShadow: 'none' },
                    }}
                >
                    <ArrowUpward />
                </Button>
                <Button variant="contained" onClick={onArrowDownClick}
                    sx={{
                        width: '100%',
                        minWidth: 0,
                        height: 48,
                        maxHeight: 48,
                        padding: 0,
                        borderRadius: 2,
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'primary.light',
                        border: '1px solid',
                        borderColor: 'primary.dark',
                        boxShadow: 'none',
                        color: 'white',
                        '&:hover': { backgroundColor: 'primary.main', boxShadow: 'none' },
                    }}
                >
                    <ArrowDownward />
                </Button>
                <Button
                    variant="contained"
                    onClick={handleForceSave}
                    aria-label="Save hole scores"
                    sx={{
                        width: '100%',
                        minWidth: 0,
                        height: 40,
                        maxHeight: 40,
                        padding: 0,
                        borderRadius: 2,
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'primary.main',
                        border: '1px solid',
                        borderColor: 'primary.dark',
                        boxShadow: 'none',
                        color: 'white',
                        '&:hover': { backgroundColor: 'primary.dark', boxShadow: 'none' },
                    }}
                >
                    Save
                </Button>
            </Stack>

        </Stack>
    );
};

export default NumPad;